import {
  getDataForItemGrid,
  getOrdersByCompanyId,
  getSumByFilter,
  getTop5CustomersByFilter,
  getTop5ProductsByFilter, // Import the function for top 5 products
} from '@/services/Orders/orders.services';
import { FinancialGridResponse, Order, OrderFilter } from '@/types/orders.type';

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

interface GetOrdersParams {
  companyId: string;
  sort?: { [key: string]: number };
  limit?: number;
  outlets?: string[];
}

type GraphDataParams = {
  companyId: string;
  filter: OrderFilter;
  outlets: string[] | [];
};

export const ordersSlice = createApi({
  reducerPath: 'orders',
  tagTypes: ['orders'],
  baseQuery: fetchBaseQuery(),
  endpoints: (builder) => ({
    getOrders: builder.query<Order[] | [], GetOrdersParams>({
      queryFn: async ({ companyId, sort, limit, outlets = [] }) => {
        const data = await getOrdersByCompanyId(companyId, sort, limit, outlets);
        return { data: data || [] };
      },
      providesTags: ['orders'],
    }),
    getGraphData: builder.query<any, GraphDataParams>({
      queryFn: async ({ companyId, filter, outlets }) => {
        const data = await getSumByFilter(companyId, filter, outlets);
        return { data: data || [] };
      },
    }),
    getDataForFinancesGrid: builder.query<FinancialGridResponse | undefined, GraphDataParams>({
      queryFn: async ({ companyId, filter, outlets }) => {
        const data = await getDataForItemGrid(companyId, filter, outlets);
        return { data: data || undefined, error: undefined, meta: undefined };
      },
    }),
    getTop5Products: builder.query<
      | {
          productId: string;
          name: string;
          totalSales: number;
          quantitySold: number;
        }[]
      | undefined,
      GraphDataParams
    >({
      queryFn: async ({ companyId, filter, outlets }) => {
        const data = await getTop5ProductsByFilter(companyId, filter, outlets);
        return { data: data || undefined, error: undefined, meta: undefined };
      },
    }),
    getTop5Customers: builder.query<
      | {
          customerId: string;
          firstName: string;
          lastName: string;
          totalSpent: number;
          orderCount: number;
        }[]
      | undefined,
      GraphDataParams
    >({
      queryFn: async ({ companyId, filter, outlets }) => {
        const data = await getTop5CustomersByFilter(companyId, filter, outlets);
        return { data: data || undefined, error: undefined, meta: undefined };
      },
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetGraphDataQuery,
  useGetDataForFinancesGridQuery,
  useGetTop5ProductsQuery,
  useGetTop5CustomersQuery,
} = ordersSlice;
